import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listWebdav, importWebdav, type WebdavItem } from "../api/webdav";

interface Props {
  /** Active session id, or null in hero mode (no session yet). When set, the
   *  browser imports immediately; in hero mode it stashes the selection for
   *  ChatPage to import once the first message creates the session. */
  sessionId: string | null;
  /** Hero-mode handoff: the selected items to import after session creation. */
  onStash: (items: WebdavItem[]) => void;
  /** Called after a successful active-session import. */
  onImported: () => void;
  onClose: () => void;
}

function fmtSize(bytes: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

/**
 * Virtual explorer over the WebDAV document library. Navigate folders,
 * tick files/folders across multiple directories, then «Добавить» pulls the
 * selection into the agent workspace. Folder picks are imported recursively
 * by the backend.
 */
export function WebdavBrowser({ sessionId, onStash, onImported, onClose }: Props) {
  const [path, setPath] = useState("");
  // Selection persists across navigation: rel-path → isDir.
  const [selected, setSelected] = useState<Map<string, boolean>>(new Map());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: entries = [], isLoading, isError } = useQuery({
    queryKey: ["webdav", path],
    queryFn: () => listWebdav(path),
    staleTime: 30_000,
  });

  const crumbs = useMemo(() => {
    const parts = path.split("/").filter(Boolean);
    const acc: { label: string; path: string }[] = [{ label: "Сетевая папка", path: "" }];
    let cur = "";
    for (const p of parts) {
      cur = cur ? `${cur}/${p}` : p;
      acc.push({ label: p, path: cur });
    }
    return acc;
  }, [path]);

  const toggle = (entry: { path: string; isDir: boolean }) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(entry.path)) next.delete(entry.path);
      else next.set(entry.path, entry.isDir);
      return next;
    });
  };

  const items: WebdavItem[] = useMemo(
    () => [...selected].map(([p, isDir]) => ({ path: p, isDir })),
    [selected],
  );

  const confirm = async () => {
    if (items.length === 0) return;
    if (!sessionId) {
      onStash(items);
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await importWebdav(sessionId, items);
      onImported();
      onClose();
    } catch (e) {
      setError((e as Error).message || "Не удалось импортировать");
      setBusy(false);
    }
  };

  return (
    <div className="wdav-backdrop" onClick={onClose}>
      <div className="wdav-dialog" onClick={(e) => e.stopPropagation()}>
        <header className="wdav-head">
          <h2 className="wdav-title">Сетевая папка</h2>
          <button className="wdav-close" onClick={onClose} aria-label="Закрыть">×</button>
        </header>

        {crumbs.length > 1 && (
        <nav className="wdav-crumbs">
          {crumbs.map((c, i) => (
            <span key={c.path} className="wdav-crumb">
              {i > 0 && <span className="wdav-crumb-sep">/</span>}
              <button
                type="button"
                className="wdav-crumb-btn"
                disabled={i === crumbs.length - 1}
                onClick={() => setPath(c.path)}
              >
                {c.label}
              </button>
            </span>
          ))}
        </nav>
        )}

        <div className="wdav-list">
          {isLoading ? (
            <div className="wdav-empty">Загрузка…</div>
          ) : isError ? (
            <div className="wdav-empty">Не удалось открыть папку</div>
          ) : entries.length === 0 ? (
            <div className="wdav-empty">Папка пуста</div>
          ) : (
            entries.map((e) => (
              <div key={e.path} className="wdav-row">
                <input
                  type="checkbox"
                  className="wdav-check"
                  checked={selected.has(e.path)}
                  onChange={() => toggle(e)}
                />
                <button
                  type="button"
                  className={`wdav-entry ${e.isDir ? "wdav-entry--dir" : "wdav-entry--file"}`}
                  onClick={() => (e.isDir ? setPath(e.path) : toggle(e))}
                  title={e.name}
                >
                  <span className={`wdav-icon ${e.isDir ? "wdav-icon-dir" : "wdav-icon-file"}`} />
                  <span className="wdav-name">{e.name}</span>
                  {!e.isDir && e.size > 0 && <span className="wdav-size">{fmtSize(e.size)}</span>}
                  {e.isDir && <span className="wdav-chevron">›</span>}
                </button>
              </div>
            ))
          )}
        </div>

        {error && <div className="wdav-error">{error}</div>}

        <footer className="wdav-foot">
          <span className="wdav-selected">
            {items.length > 0 ? `Выбрано: ${items.length}` : "Отметьте файлы или папки"}
          </span>
          <div className="wdav-foot-actions">
            <button type="button" className="wdav-btn wdav-btn--ghost" onClick={onClose}>
              Отмена
            </button>
            <button
              type="button"
              className="wdav-btn wdav-btn--primary"
              disabled={items.length === 0 || busy}
              onClick={confirm}
            >
              {busy ? "Добавляю…" : "Добавить"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
